// ============================================
// SHIP SELECT SCREEN - 機体選択画面の描画とロジック
// ============================================

import { getP5 } from '../../core/p5-instance';
import { CONFIG } from '../../config/game-config';
import {
    getSelectedShip,
    setSelectedShip,
    getSelectedDifficulty,
    setSelectedDifficulty,
    getTitleTimer,
    getShipSelectEffect,
    setShipSelectEffect,
    getLaunchEffect,
    setLaunchEffect,
    setGameState
} from '../../store/index';
import type { Difficulty, ShipType } from '../../types/game-types';
import { resetGame } from '../../engine/reset';
import { drawShipBomber, drawShipHunter, drawShipLancer } from '../../features/player/renderer/ship-renderer';
import {
    drawPhase1,
    drawPhase2,
    drawPhase3,
    updatePhase2Particles,
    isLaunchComplete,
    getCurrentPhase,
    getPhaseProgress
} from './animations/ship-launch-effects';

// ============================================
// drawShipSelectScreen() - 機体選択画面の描画（難易度選択統合）
// ============================================
export function drawShipSelectScreen() {
    const p = getP5();
    const selectedShip = getSelectedShip();
    const selectedDifficulty = getSelectedDifficulty();
    const titleTimer = getTitleTimer();
    const shipSelectEffect = getShipSelectEffect();
    const launchEffect = getLaunchEffect();

    p.textAlign(p.CENTER, p.CENTER);
    p.textFont('Orbitron');

    // 背景オーバーレイ（半透明）
    p.fill(0, 0, 20, 150);
    p.rect(0, 0, p.width, p.height);

    // カード位置計算（3機体対応）- BOMBERを中央に配置
    const cardW = 120;
    const cardH = 220;
    const cardY = 75;
    const cardSpacing = 8;
    const totalWidth = cardW * 3 + cardSpacing * 2;
    const startX = (p.width - totalWidth) / 2;

    // 左:HUNTER, 中央:BOMBER, 右:LANCER
    const hunter_cardX = startX;
    const bomber_cardX = startX + cardW + cardSpacing;
    const lancer_cardX = startX + (cardW + cardSpacing) * 2;
    const shipPreviewY = cardY + 70;

    // 出撃エフェクト中
    if (launchEffect && launchEffect.active) {
        drawLaunchEffect3(hunter_cardX, bomber_cardX, lancer_cardX, cardW, shipPreviewY);
        return;
    }

    // タイトル
    p.fill(0, 255, 255);
    p.textSize(22);
    p.text('SELECT SHIP', p.width / 2, 38);

    // 区切り線
    p.stroke(0, 255, 255, 100);
    p.strokeWeight(1);
    p.line(30, 58, p.width - 30, 58);
    p.noStroke();

    // HUNTER（左）
    drawShipCard(hunter_cardX, cardY, cardW, cardH, 'hunter', selectedShip === 'hunter');

    // BOMBER（中央）
    drawShipCard(bomber_cardX, cardY, cardW, cardH, 'bomber', selectedShip === 'bomber');

    // LANCER（右）
    drawShipCard(lancer_cardX, cardY, cardW, cardH, 'lancer', selectedShip === 'lancer');

    // 選択エフェクト描画
    if (shipSelectEffect) {
        drawShipSelectEffect();
    }

    // --- 難易度選択セクション ---
    const diffY = 340;

    // 難易度ラベル
    p.fill(150);
    p.textSize(12);
    p.textAlign(p.CENTER, p.CENTER);
    p.text('DIFFICULTY', p.width / 2, diffY - 22);

    // 難易度ボタン配置（固定幅で中央揃え）
    const diffBtnW = 80;
    const diffBtnH = 34;
    const diffSpacing = 12;

    // 全体の幅を固定で計算
    const diffTotalWidth = diffBtnW * 3 + diffSpacing * 2;
    const startDiffX = p.width / 2 - diffTotalWidth / 2;

    // EASY（左）
    const easyX = startDiffX;
    drawDifficultyButton(easyX, diffY, diffBtnW, diffBtnH, 'easy', selectedDifficulty === 'easy');

    // NORMAL（中央）
    const normalX = startDiffX + diffBtnW + diffSpacing;
    drawDifficultyButton(normalX, diffY, diffBtnW, diffBtnH, 'normal', selectedDifficulty === 'normal');

    // HARD（右）
    const hardX = startDiffX + (diffBtnW + diffSpacing) * 2;
    drawDifficultyButton(hardX, diffY, diffBtnW, diffBtnH, 'hard', selectedDifficulty === 'hard');

    // 選択中の難易度説明（日本語・枠囲み）
    const descY = diffY + diffBtnH + 32;
    const diffDesc = getDifficultyDescription(selectedDifficulty);
    const descBoxW = 280;
    const descBoxH = 36;

    // 説明枠（実線で情報エリアを明示）
    p.stroke(100);
    p.strokeWeight(1);
    p.fill(20, 20, 35, 180);
    p.rect(p.width / 2 - descBoxW / 2, descY - descBoxH / 2, descBoxW, descBoxH, 4);
    p.noStroke();

    // 説明テキスト（日本語）
    p.fill(220);
    p.textSize(12);
    p.text(diffDesc, p.width / 2, descY);

    // --- 出撃ボタン ---
    const launchBtnY = 480;
    const launchBtnW = 200;
    const launchBtnH = 55;
    const pulse = p.sin(titleTimer * 0.1) * 0.2 + 0.8;

    // グロー効果
    for (let i = 3; i > 0; i--) {
        p.fill(0, 255, 100, 20);
        p.noStroke();
        p.rect(p.width / 2 - launchBtnW / 2 - i * 3, launchBtnY - i * 3, launchBtnW + i * 6, launchBtnH + i * 6, 12);
    }

    p.fill(0, 255, 100, 200 * pulse);
    p.stroke(0, 255, 100);
    p.strokeWeight(3);
    p.rect(p.width / 2 - launchBtnW / 2, launchBtnY, launchBtnW, launchBtnH, 10);

    p.fill(255);
    p.noStroke();
    p.textSize(22);
    p.text('▶ LAUNCH', p.width / 2, launchBtnY + launchBtnH / 2);

    // ヒント
    p.fill(100);
    p.textSize(10);
    p.text('TAP SHIP TO SELECT', p.width / 2, launchBtnY + launchBtnH + 30);
}

// ============================================
// drawDifficultyButton() - 難易度ボタン描画
// ============================================
export function drawDifficultyButton(x: number, y: number, w: number, h: number, difficulty: Difficulty, isSelected: boolean) {
    const p = getP5();
    const colors: Record<Difficulty, number[]> = {
        easy: [100, 255, 100],
        normal: [255, 200, 0],
        hard: [255, 80, 80]
    };
    const labels: Record<Difficulty, string> = {
        easy: 'EASY',
        normal: 'NORMAL',
        hard: 'HARD'
    };
    const col = colors[difficulty];

    if (isSelected) {
        // 選択中 - グロー効果
        for (let i = 2; i > 0; i--) {
            p.fill(col[0], col[1], col[2], 15);
            p.noStroke();
            p.rect(x - i * 2, y - i * 2, w + i * 4, h + i * 4, 8);
        }
        p.fill(col[0], col[1], col[2], 80);
        p.stroke(col[0], col[1], col[2]);
        p.strokeWeight(2);
    } else {
        // 非選択
        p.fill(40, 40, 60, 200);
        p.stroke(80);
        p.strokeWeight(1);
    }
    p.rect(x, y, w, h, 6);

    // ラベル
    if (isSelected) {
        p.fill(col[0], col[1], col[2]);
    } else {
        p.fill(120);
    }
    p.noStroke();
    p.textSize(13);
    p.text(labels[difficulty], x + w / 2, y + h / 2);
}

// ============================================
// getDifficultyDescription() - 難易度説明取得（日本語）
// ============================================
export function getDifficultyDescription(difficulty: Difficulty) {
    switch (difficulty) {
        case 'easy': return '敵の動きが遅め｜特殊兵装 5個';
        case 'normal': return 'バランス型｜特殊兵装 3個';
        case 'hard': return '敵が速く硬い｜特殊兵装 2個';
        default: return '';
    }
}

// ============================================
// drawShipSelectEffect() - 選択時の控えめなエフェクト描画
// ============================================
export function drawShipSelectEffect() {
    const p = getP5();
    const shipSelectEffect = getShipSelectEffect();
    if (!shipSelectEffect) {
        return;
    }

    shipSelectEffect.timer--;
    if (shipSelectEffect.timer <= 0) {
        setShipSelectEffect(null);
        return;
    }

    const progress = 1 - (shipSelectEffect.timer / 20);
    const shipType = shipSelectEffect.type as ShipType;
    const cfg = CONFIG.ships[shipType];

    // リング拡大エフェクト
    p.noFill();
    p.stroke(cfg.color[0], cfg.color[1], cfg.color[2], 200 * (1 - progress));
    p.strokeWeight(2);
    p.circle(shipSelectEffect.x, shipSelectEffect.y, 50 + progress * 40);

    // 小さなパーティクル
    if (shipSelectEffect.timer > 10) {
        for (let i = 0; i < 4; i++) {
            const angle = (p.TWO_PI / 4) * i + progress * p.PI;
            const dist = 20 + progress * 30;
            p.fill(cfg.color[0], cfg.color[1], cfg.color[2], 150 * (1 - progress));
            p.noStroke();
            p.circle(
                shipSelectEffect.x + p.cos(angle) * dist,
                shipSelectEffect.y + p.sin(angle) * dist,
                4
            );
        }
    }
}

// ============================================
// drawLaunchEffect3() - 出撃エフェクト描画（機体が中央に移動して出撃）
// 3機体対応の出撃エフェクト
// ============================================
export function drawLaunchEffect3(hunter_cardX: number, bomber_cardX: number, lancer_cardX: number, cardW: number, shipPreviewY: number) {
    const p = getP5();
    const launchEffect = getLaunchEffect();
    if (!launchEffect) {
        return;
    }
    const selectedShip = getSelectedShip();
    launchEffect.timer++;

    const cfg = CONFIG.ships[selectedShip];

    // 選択機体の元の位置
    let startX: number;
    if (selectedShip === 'bomber') {
        startX = bomber_cardX + cardW / 2;
    } else if (selectedShip === 'hunter') {
        startX = hunter_cardX + cardW / 2;
    } else {
        startX = lancer_cardX + cardW / 2;
    }

    const startY = shipPreviewY;
    const centerX = p.width / 2;
    const centerY = p.height / 2;

    // 機体描画ヘルパー
    function drawSelectedShip(size: number) {
        if (selectedShip === 'bomber') {
            drawBomberPreview(0, 0, size);
        } else if (selectedShip === 'hunter') {
            drawHunterPreview(0, 0, size);
        } else {
            drawLancerPreview(0, 0, size);
        }
    }

    // 非選択カードを描画するコールバック
    const drawFadingCards = (alpha: number) => {
        const ships: ShipType[] = ['hunter', 'bomber', 'lancer'];
        const cardXs = [hunter_cardX, bomber_cardX, lancer_cardX];
        for (let i = 0; i < ships.length; i++) {
            if (ships[i] !== selectedShip) {
                drawFadingCard(cardXs[i], 85, cardW, 210, ships[i], alpha);
            }
        }
    };

    // フェーズ判定とプログレス計算（ship-launch-effects ユーティリティに委譲）
    const phase = getCurrentPhase(launchEffect.timer);
    const progress = getPhaseProgress(launchEffect.timer, phase);

    switch (phase) {
        case 1:
            // フェーズ1: UIフェードアウト + 機体移動
            drawPhase1({
                fadeProgress: progress,
                startX,
                startY,
                centerX,
                centerY,
                drawShip: drawSelectedShip,
                drawFadingCards
            });
            break;

        case 2:
            // フェーズ2: 中央でチャージ
            // パーティクル更新（状態変更）
            if (launchEffect.particles) {
                updatePhase2Particles(
                    launchEffect.particles,
                    centerX,
                    centerY,
                    cfg.color,
                    launchEffect.timer
                );
            }

            // Phase 2 の描画を委譲
            drawPhase2({
                chargeProgress: progress,
                centerX,
                centerY,
                timer: launchEffect.timer,
                color: cfg.color,
                particles: launchEffect.particles || [],
                drawShip: drawSelectedShip
            });
            break;

        case 3:
            // フェーズ3: 出撃
            drawPhase3({
                launchProgress: progress,
                centerX,
                centerY,
                color: cfg.color,
                shipType: selectedShip,
                drawShip: drawSelectedShip
            });
            break;
    }

    // 完了判定（ship-launch-effects モジュールに委譲）
    if (isLaunchComplete(launchEffect.timer)) {
        setLaunchEffect(null);
        resetGame();
        setGameState('playing');
    }
}

// ============================================
// drawFadingCard() - フェードするカード描画
// ============================================
export function drawFadingCard(x: number, y: number, w: number, h: number, shipType: ShipType, alpha: number) {
    const p = getP5();
    const cfg = CONFIG.ships[shipType];

    p.fill(30, 30, 50, alpha * 0.8);
    p.stroke(100, 100, 100, alpha);
    p.strokeWeight(1);
    p.rect(x, y, w, h, 10);

    p.fill(cfg.color[0], cfg.color[1], cfg.color[2], alpha);
    p.noStroke();
    p.textSize(18);
    p.text(cfg.name, x + w / 2, y + 25);

    p.push();
    p.translate(x + w / 2, y + 100);
    if (shipType === 'bomber') {
        drawBomberPreview(0, 0, 50);
    } else if (shipType === 'hunter') {
        drawHunterPreview(0, 0, 50);
    } else if (shipType === 'lancer') {
        drawLancerPreview(0, 0, 50);
    }
    p.pop();
}

// ============================================
// Easing Functions - イージング関数
// ============================================
export function easeInOutCubic(t: number) {
    const p = getP5();
    return t < 0.5 ? 4 * t * t * t : 1 - p.pow(-2 * t + 2, 3) / 2;
}

export function easeInCubic(t: number) {
    return t * t * t;
}

// ============================================
// triggerShipSelectEffect() - 機体選択トリガー
// ============================================
export function triggerShipSelectEffect(shipType: ShipType, x: number, y: number) {
    setShipSelectEffect({
        x: x,
        y: y,
        timer: 20,
        type: shipType
    });
}

// ============================================
// triggerLaunchEffect() - 出撃エフェクトトリガー
// ============================================
export function triggerLaunchEffect() {
    setLaunchEffect({
        timer: 0,
        active: true,
        particles: []
    });
}

// ============================================
// drawShipCard() - 機体カード描画
// ============================================
export function drawShipCard(x: number, y: number, w: number, h: number, shipType: ShipType, isSelected: boolean) {
    const p = getP5();
    const cfg = CONFIG.ships[shipType];
    const titleTimer = getTitleTimer();

    // カード背景
    if (isSelected) {
        p.fill(cfg.color[0], cfg.color[1], cfg.color[2], 40);
        p.stroke(cfg.color[0], cfg.color[1], cfg.color[2]);
        p.strokeWeight(3);
    } else {
        p.fill(30, 30, 50, 200);
        p.stroke(100);
        p.strokeWeight(1);
    }
    p.rect(x, y, w, h, 8);

    // 機体名
    p.fill(cfg.color[0], cfg.color[1], cfg.color[2]);
    p.noStroke();
    p.textAlign(p.CENTER, p.CENTER);
    p.textSize(14);
    p.text(cfg.name, x + w / 2, y + 20);

    // 機体プレビュー
    p.push();
    p.translate(x + w / 2, y + 70);
    const hover = p.sin(titleTimer * 0.05) * 3;
    p.translate(0, hover);

    if (shipType === 'bomber') {
        drawBomberPreview(0, 0, 38);
    } else if (shipType === 'hunter') {
        drawHunterPreview(0, 0, 38);
    } else if (shipType === 'lancer') {
        drawLancerPreview(0, 0, 38);
    }
    p.pop();

    // 説明
    p.fill(200);
    p.textAlign(p.CENTER, p.CENTER);
    p.textSize(9);
    p.text(cfg.description, x + w / 2, y + 115);

    // アビリティエリア
    p.fill(cfg.color[0], cfg.color[1], cfg.color[2], 80);
    p.noStroke();
    p.rect(x + 6, y + 128, w - 12, 38, 5);

    p.fill(255);
    p.textAlign(p.CENTER, p.CENTER);
    p.textSize(10);
    if (shipType === 'bomber') {
        p.text('BOMB', x + w / 2, y + 140);
        p.text('全体攻撃+弾消し', x + w / 2, y + 154);
    } else if (shipType === 'hunter') {
        p.text('HOMING', x + w / 2, y + 140);
        p.text('追尾弾(10秒)', x + w / 2, y + 154);
    } else if (shipType === 'lancer') {
        p.text('CANNON', x + w / 2, y + 140);
        p.text('荷電粒子砲', x + w / 2, y + 154);
    }

    // 機体ステータス表示
    drawShipStats(x, y + 175, w, shipType);
}

// ============================================
// drawShipStats() - 機体ステータス描画関数
// ============================================
export function drawShipStats(x: number, y: number, w: number, shipType: ShipType): void {
    const p = getP5();
    // 各機体のステータスをCONFIGから取得（S=5, A=4, B=3, C=2, D=1）
    const stats = CONFIG.ships[shipType].stats;

    const rankLabels = ['D', 'C', 'B', 'A', 'S'];
    const rankColors = [
        [150, 150, 150], // D - グレー
        [100, 200, 100], // C - 緑
        [100, 150, 255], // B - 青
        [255, 200, 50],  // A - 金
        [255, 100, 100]  // S - 赤
    ];

    const statNames = ['POWER', 'MOBILITY', 'SHIELD', 'STABILITY'];
    const statValues = [stats.power, stats.mobility, stats.shield, stats.stability];

    const lineHeight = 11;
    const centerX = x + w / 2;

    for (let i = 0; i < 4; i++) {
        const statY = y + i * lineHeight;
        const val = statValues[i];
        const rank = rankLabels[val - 1];
        const rankCol = rankColors[val - 1];

        // ステータス名（左寄せ）
        p.textAlign(p.LEFT, p.CENTER);
        p.textSize(7);
        p.fill(180);
        p.text(statNames[i], centerX - 55, statY);

        // ランク（中央）
        p.textAlign(p.CENTER, p.CENTER);
        p.textSize(9);
        p.fill(rankCol[0], rankCol[1], rankCol[2]);
        p.text(rank, centerX - 10, statY);

        // バー（右側）
        p.textAlign(p.LEFT, p.CENTER);
        p.textSize(8);
        const barX = centerX;
        let barStr = '';
        for (let j = 0; j < 5; j++) {
            if (j < val) {
                barStr += '█';
            } else {
                barStr += '░';
            }
        }
        p.fill(rankCol[0], rankCol[1], rankCol[2]);
        p.text(barStr, barX, statY);
    }

    // textAlignをリセット
    p.textAlign(p.CENTER, p.CENTER);
}

// ============================================
// drawBomberPreview() - BOMBERプレビュー
// ============================================
export function drawBomberPreview(x: number, y: number, size: number): void {
    drawShipBomber(x, y, size, {
        showEngine: false,
        showGlow: false
    });
}

// ============================================
// drawHunterPreview() - HUNTERプレビュー
// ============================================
export function drawHunterPreview(x: number, y: number, size: number): void {
    drawShipHunter(x, y, size, {
        showEngine: false,
        showGlow: false
    });
}

// ============================================
// drawLancerPreview() - LANCERプレビュー（二股の先端 + 三角キャノピー）
// ============================================
export function drawLancerPreview(x: number, y: number, size: number): void {
    drawShipLancer(x, y, size, {
        showEngine: false,
        showGlow: false
    });
}

// ============================================
// handleShipSelect() - 機体選択のクリック判定
// ============================================
export function handleShipSelect() {
    const p = getP5();
    const launchEffect = getLaunchEffect();
    const selectedShip = getSelectedShip();

    // 出撃エフェクト中はクリック無視
    if (launchEffect && launchEffect.active) {
        return false;
    }

    // カード位置計算（3機体対応）- BOMBERを中央に配置
    const cardW = 120;
    const cardH = 220;
    const cardY = 75;
    const cardSpacing = 8;
    const totalWidth = cardW * 3 + cardSpacing * 2;
    const startX = (p.width - totalWidth) / 2;

    // HUNTERカード（左）
    const hunter_x = startX;
    if (p.mouseX >= hunter_x && p.mouseX <= hunter_x + cardW &&
        p.mouseY >= cardY && p.mouseY <= cardY + cardH) {
        if (selectedShip !== 'hunter') {
            setSelectedShip('hunter');
            triggerShipSelectEffect('hunter', hunter_x + cardW / 2, cardY + 70);
        }
        return true;
    }

    // BOMBERカード（中央）
    const bomber_x = startX + cardW + cardSpacing;
    if (p.mouseX >= bomber_x && p.mouseX <= bomber_x + cardW &&
        p.mouseY >= cardY && p.mouseY <= cardY + cardH) {
        if (selectedShip !== 'bomber') {
            setSelectedShip('bomber');
            triggerShipSelectEffect('bomber', bomber_x + cardW / 2, cardY + 70);
        }
        return true;
    }

    // LANCERカード（右）
    const lancer_x = startX + (cardW + cardSpacing) * 2;
    if (p.mouseX >= lancer_x && p.mouseX <= lancer_x + cardW &&
        p.mouseY >= cardY && p.mouseY <= cardY + cardH) {
        if (selectedShip !== 'lancer') {
            setSelectedShip('lancer');
            triggerShipSelectEffect('lancer', lancer_x + cardW / 2, cardY + 70);
        }
        return true;
    }

    // --- 難易度ボタン（固定幅で中央揃え）---
    const diffY = 340;
    const diffBtnW = 80;
    const diffBtnH = 34;
    const diffSpacing = 12;

    // 全体の幅を固定で計算
    const diffTotalW = diffBtnW * 3 + diffSpacing * 2;
    const startDiffX = p.width / 2 - diffTotalW / 2;

    // EASY
    const easyX = startDiffX;
    if (p.mouseX >= easyX && p.mouseX <= easyX + diffBtnW &&
        p.mouseY >= diffY && p.mouseY <= diffY + diffBtnH) {
        setSelectedDifficulty('easy');
        return true;
    }

    // NORMAL
    const normalX = startDiffX + diffBtnW + diffSpacing;
    if (p.mouseX >= normalX && p.mouseX <= normalX + diffBtnW &&
        p.mouseY >= diffY && p.mouseY <= diffY + diffBtnH) {
        setSelectedDifficulty('normal');
        return true;
    }

    // HARD
    const hardX = startDiffX + (diffBtnW + diffSpacing) * 2;
    if (p.mouseX >= hardX && p.mouseX <= hardX + diffBtnW &&
        p.mouseY >= diffY && p.mouseY <= diffY + diffBtnH) {
        setSelectedDifficulty('hard');
        return true;
    }

    // LAUNCHボタン（出撃エフェクト開始）
    const launchBtnY = 480;
    const launchBtnW = 200;
    const launchBtnH = 55;
    if (p.mouseX >= p.width / 2 - launchBtnW / 2 && p.mouseX <= p.width / 2 + launchBtnW / 2 &&
        p.mouseY >= launchBtnY && p.mouseY <= launchBtnY + launchBtnH) {
        triggerLaunchEffect();
        return 'launching';
    }

    return false;
}
